import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * useApiKeys
 *
 * Lista, cria e revoga as chaves de API do usuário autenticado.
 *
 * A criação foge do padrão dos outros hooks de CRUD: além de invalidar o
 * cache, ela DEVOLVE o segredo em claro para quem chamou. É a única vez que
 * esse valor existe — o backend guarda só o hash —, então a tela precisa
 * recebê-lo e exibi-lo na hora.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeyService } from '@/services/apiKeyService';
import { CreatedApiKey } from '@/types/apiKey';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export function useApiKeys(options: { enabled?: boolean } = {}) {
  useLanguage();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { enabled = true } = options;

  // O backend deriva o dono do token, então o userId não vai na requisição —
  // mas vai na chave de cache. O QueryClient é criado em escopo de módulo e
  // sobrevive à troca de usuário quando ela acontece por navegação de SPA (o
  // logout entre abas não recarrega a página). Sem o id aqui, a lista de quem
  // saiu apareceria por até 5 minutos para quem entrou.
  const userId = user?.id || user?.id_user;
  const queryKey = ['api-keys', userId];

  const {
    data: apiKeys = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => apiKeyService.getApiKeys(),
    enabled: enabled && !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => apiKeyService.createApiKey({ name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || translateInterfaceText("Erro ao criar a chave");
      toast.error(errorMessage);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => apiKeyService.revokeApiKey(keyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
      toast.success(translateInterfaceText("Chave revogada"));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || translateInterfaceText("Erro ao revogar a chave");
      toast.error(errorMessage);
    },
  });

  /**
   * Cria e devolve a chave com o segredo. Sem toast de sucesso de propósito:
   * quem chama abre o modal que mostra o valor, e um toast por cima seria
   * ruído no momento em que o usuário precisa copiar.
   */
  const createApiKey = async (name: string): Promise<CreatedApiKey> => {
    return createMutation.mutateAsync(name);
  };

  const revokeApiKey = async (keyId: string) => {
    return revokeMutation.mutateAsync(keyId);
  };

  return {
    apiKeys,
    isLoading,
    isFetching,
    isError,
    error,
    isCreating: createMutation.isPending,
    isRevoking: revokeMutation.isPending,
    createApiKey,
    revokeApiKey,
    refetch,
  };
}

export default useApiKeys;
